import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/contact/submit`, {
        ...formData,
        language: i18n.language
      });
      
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-stone-50 min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-sans font-semibold mb-4">
            {t('contact.title')}
          </h1>
          <p className="text-lg text-stone-600 font-light max-w-2xl mx-auto">
            We're here to help. Send us a message or reach out via phone or WhatsApp.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-white p-8 rounded-sm border border-stone-200">
            <h2 className="text-2xl font-sans font-semibold mb-6">Send Us a Message</h2>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">
                  {t('contact.name')} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">
                  {t('contact.email')} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">
                  {t('contact.phone')}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-phone"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-stone-700 mb-2">
                  {t('contact.message')} *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full p-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-message"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary hover:bg-primary/90 text-white py-4 rounded-sm transition-colors font-semibold disabled:opacity-50 flex items-center justify-center"
                data-testid="contact-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    {t('contact.send')}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Office */}
            <div className="bg-white p-8 rounded-sm border border-stone-200">
              <h2 className="text-2xl font-sans font-semibold mb-6">{t('contact.office')}</h2>
              <div className="space-y-4">
                <div className="flex items-start">
                  <MapPin className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <p className="font-semibold text-stone-900 mb-1">InFox Group of Companies</p>
                    <p className="text-stone-600 font-light">
                      {t('contact.officeAddress')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <p className="font-semibold text-stone-900 mb-1">{t('footer.phone')}</p>
                    <a
                      href="tel:+302314033222"
                      className="text-primary hover:underline"
                    >
                      +30 2314 033 222
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <MessageCircle className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <p className="font-semibold text-stone-900 mb-1">{t('footer.whatsapp')}</p>
                    <a
                      href="https://wa.me/306936547334"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      +30 6936 547 334
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <Mail className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
                  <div className="ml-4">
                    <p className="font-semibold text-stone-900 mb-1">{t('footer.email')}</p>
                    <a
                      href="mailto:infoxsuites@info-x.gr"
                      className="text-primary hover:underline"
                    >
                      infoxsuites@info-x.gr
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white p-8 rounded-sm border border-stone-200">
              <h2 className="text-2xl font-sans font-semibold mb-6">Visit Our Office</h2>
              <div className="aspect-video bg-stone-100 rounded-sm flex items-center justify-center">
                <a
                  href="https://maps.app.goo.gl/24zN6dNBu9e7u79q7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center"
                >
                  <MapPin className="w-5 h-5 mr-2" />
                  View on Google Maps
                </a>
              </div>
              <p className="text-sm text-stone-600 font-light mt-4">
                👜 You can store luggage or make payments at our office during business hours.
              </p>
            </div>

            {/* Business Hours */}
            <div className="bg-primary/5 p-6 rounded-sm border border-primary/20">
              <h3 className="font-semibold text-stone-900 mb-3">🕒 Business Hours</h3>
              <div className="text-sm text-stone-700 space-y-1">
                <p>Monday - Friday: 9:00 - 17:00</p>
                <p>Saturday: By appointment</p>
                <p>Sunday: Closed</p>
              </div>
              <p className="text-xs text-stone-600 mt-3 font-light">
                For urgent matters outside business hours, please contact us via WhatsApp.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
